import React from 'react';
import StaffProfileTemplate, {
  ProfileSection,
  ProfileParagraphs,
  ProfileBullets,
  ProfilePublications,
  ProfileContactSection,
} from './StaffProfileTemplate';

const AdityaKiranKakatiProfile = () => {
  const person = {
    id: 'aditya-kiran-kakati',
    name: 'Aditya Kiran Kakati',
    honorific: 'Dr',
    title: 'Senior Research Associate & Programme Lead (Global Research Outreach and Capacity-Building Initiatives)',
    affiliation: 'The Highland Institute; International Institute for Asian Studies (IIAS), Leiden',
    image: 'https://customer-assets.emergentagent.com/job_vegr-highland/artifacts/pnoj5z7w_Aditya%20Kiran%20Kakati.jpg',
    email: 'aditya.kakati@iheid.ch',
    category: 'Fellows & Visiting Scholars',
    testid: 'aditya-kiran-kakati-profile',
  };

  return (
    <StaffProfileTemplate person={person}>
      {/* Intellectual Biography */}
      <ProfileSection title="Intellectual Biography">
        <ProfileParagraphs
          items={[
            'Aditya Kiran Kakati holds a PhD in International History with a minor in Anthropology and Sociology from the Graduate Institute, Geneva. His intellectual journey has evolved at the intersections of global history, historical anthropology, sociology, and political geography, shaped by a sustained engagement with questions of war, borders, sovereignty, and marginality in Asia\'s highland frontiers.',
            'His current research is supported by two major grants: a prestigious Wenner-Gren Foundation fellowship for completing a monograph on the Second World War and the remaking of governance in the Indo–Myanmar highlands, and a Delta on the Move Foundation grant for War Residues, a collaborative project examining war remains, objects, oral histories, and memory in the Eastern Nagaland–Myanmar borderlands.',
            'Together, these projects explore how the material and affective afterlives of war continue to shape political identities, social memory, and state-society relations in frontier regions. In a voluntary capacity, he currently leads Research Programmes and Global Engagement at The Highland Institute, Kohima, serves as Associate Editor of The Highlander Journal, co-edits the Global Health Matters blog, and is co-founder of the Healing Through Humanity Network.',
            'He previously taught anthropology and sociology at the University of Groningen, and from September 2026, he teaches in the International Relations and International Organization department.',
          ]}
        />
      </ProfileSection>

      {/* Current Positions */}
      <ProfileSection title="Current Positions & Affiliations">
        <ProfileBullets
          items={[
            'Affiliate Research Fellow, International Institute for Asian Studies (IIAS), Leiden, The Netherlands',
            'Senior Research Associate & Programme Lead (Global Research, Outreach and Capacity-Building), The Highland Institute, Kohima, Nagaland',
            'Wenner Gren Foundation, Hunt Postdoctoral Fellow (March 2026)',
            'Project P.I. and Lead, War Residues: Repurposed Objects and Memories of a Global War along the Highland Naga-Myanmar Border (Delta on the Move Foundation, December 2025)',
            'Associate Editor, The Highlander Journal (since September 2020)',
            'Co-Founder of the Healing Through Humanity Network and Co-Editor, Global Health Matters blog',
          ]}
        />
      </ProfileSection>

      {/* Research Foci & Areas of Work */}
      <ProfileSection title="Research Foci & Areas of Work">
        <ProfileParagraphs
          items={[
            'Over the past decade, his research has developed around three interrelated thematic concerns:',
          ]}
        />
        <ProfileBullets
          items={[
            'Intersections of citizenship, cultural identity, and political representation—particularly through indigeneity, self-determination, religion, racial difference, and the cultural politics of food',
            'Histories and legacies of war, violent conflict, resistance, and memory—focusing on how global wars and imperial formations reshaped colonial frontiers and continue to structure postcolonial borderland societies',
            'Highland Asia through the conceptual lens of Zomia—treating upland regions as sites of alternative cartographies, epistemologies, sovereignties, and resistance that challenge state-centred and Eurocentric understandings of region and power',
          ]}
        />
        <ProfileParagraphs
          items={[
            'A central thread in his scholarship is the interrogation of why frontier regions such as Zomia are repeatedly framed as inherently "violent," "remote," or "ungovernable," despite their deep entanglement in global wars, imperial infrastructures, and state-making projects. By reading textual, oral, material, visual, and community archives against the grain, he foregrounds Indigenous experiences of conflict, marginalization, negotiation, and resistance.',
          ]}
        />
      </ProfileSection>

      {/* Highland Institute Projects */}
      <ProfileSection title="Highland Institute Projects">
        <ProfileBullets
          items={[
            'Mentor, PG Certificate Program at the Highland Institute, Kohima (ongoing)',
            'Project P.I. and Lead, War Residues: Repurposed Objects and Memories of a Global War along the Highland Naga-Myanmar Border (Delta on the Move Foundation, December 2025)',
            'Associate Editor, The Highlander Journal (since September 2020)',
          ]}
        />
      </ProfileSection>

      {/* Selected Publications */}
      <ProfileSection title="Selected Publications & Knowledge Outputs">
        <h3 className="font-semibold text-stone-900 mt-2 mb-4 not-prose">Peer-Reviewed Journal Articles</h3>
        <ProfilePublications
          items={[
            {
              year: '2026',
              citation: (
                <>
                  'Gangsters or Guerrillas?: Violent Workers and Shadow Governance in India's Eastern Borderlands' in <em>Critical Military Studies</em>, Special Issue "Soldiering as Work" (July 2026, open-access).
                </>
              ),
            },
            {
              year: '2026',
              citation: (
                <>
                  'Worlds of War: Zo Memorialisation of Global and "Total Wars" in the decolonising India-Burma (Myanmar) borderlands' in <em>Journal of Imperial and Commonwealth History</em> (June 2026, open-access).
                </>
              ),
            },
            {
              year: '2026',
              citation: (
                <>
                  'Zeme Olympics and the Historical Pathways of Naga Sovereignty in Checkered Borderlands' in <em>Territory, Politics and Governance</em> (April 2026, open-access).
                </>
              ),
            },
            {
              year: '2025',
              citation: (
                <>
                  'Evading and Inviting States in "No-Man's-Lands": Headhunters in Zomia's blank spaces (1944-1964)' in <em>Modern Asian Studies</em>, 59, no. 2 (2025): 507–35 (open-access).
                </>
              ),
            },
            {
              year: '2025',
              citation: (
                <>
                  'Keeping the Edges Near: re-making remoteness and proximity in the Indo-Burmese Highland frontiers after a global war' in <em>SAMAJ: South Asia Multidisciplinary Academic Journal</em> (July 2025, open-access).
                </>
              ),
            },
            {
              year: '2024',
              citation: (
                <>
                  'Airy Infrastructures: Anxieties and Eastern Himalayan "Remoteness" (1945-1962)' in <em>Roadsides</em>, Collection 12 (November 2024, open-access).
                </>
              ),
            },
            {
              year: '2023',
              citation: (
                <>
                  'Elephant in the Room: countering cultural arguments in food-politics, racism and resistance in contemporary Naga social history' in <em>HIMALAYA</em>, Vol. 42, No. 1 (June 2023, open-access).
                </>
              ),
            },
            {
              year: '2022',
              citation: (
                <>
                  'Depth of Field: disorienting the "ethnographic gaze" through Zeme Naga realms' in <em>Anthropology and Humanism</em>, Vol. 47, No. 1 (June 2022, open-access).
                </>
              ),
            },
          ]}
        />

        <h3 className="font-semibold text-stone-900 mt-8 mb-4 not-prose">Book Chapters</h3>
        <ProfilePublications
          items={[
            {
              year: 'Forthcoming',
              citation: (
                <>
                  'Eating ethnic in urban culinary consumerism in Assam, Northeast India' in <em>Words in Use</em>, eds. Aarti Kawlra and Mohomodou Houssouba, IIAS Humanities Across Borders Methodologies Series (Leiden University Press).
                </>
              ),
            },
            {
              year: '2023',
              citation: (
                <>
                  'Limits of Sovereignty: developing blank spaces across Asian borders after the Second World War' in <em>The Oxford Handbook of Colonial Insurgencies and Counter-Insurgencies</em>, eds. Gareth Curless and Martin Thomas (Oxford University Press).
                </>
              ),
            },
            {
              year: '2022',
              citation: (
                <>
                  'Global wars in a Colonial Frontier' in <em>The Routledge Companion to Northeast India</em>, eds. T.B. Subba and Jelle J.P. Wouters (Routledge).
                </>
              ),
            },
            {
              year: '2019',
              citation: (
                <>
                  'Guns, Gifts and Guerrillas: knowledge and objects during WW2 in the Indo-Myanmar (Burma) frontier' in <em>Objects and Frontiers in Modern Asia</em>, eds. Manjeet Baruah and Lipokmar Dzüvichü (Routledge: New York).
                </>
              ),
            },
            {
              year: '2015',
              citation: (
                <>
                  'For the Love of Pork: eating in ethnic enclaves' in <em>Odysseys of Plates and Palates: Food, Society and Sociality</em>, eds. Simeon S. Magliveras and Catherine Gallin (Brill: Leiden).
                </>
              ),
            },
          ]}
        />

        <h3 className="font-semibold text-stone-900 mt-8 mb-4 not-prose">Co-Edited Special Issue</h3>
        <ProfilePublications
          items={[
            {
              year: '2025',
              citation: (
                <>
                  'Re-orienting Himalayan Borderlands: Beyond spatial fixations in South Asia' (co-edited with Mélanie Vandenhelsken and Bernardo Michael), <em>SAMAJ: South Asia Multidisciplinary Academic Journal</em> (July 2025, open-access).
                </>
              ),
            },
          ]}
        />
      </ProfileSection>

      {/* Public Engagement */}
      <ProfileSection title="Public Engagement, Teaching & Community Work">
        <ProfileParagraphs
          items={[
            'Dr. Kakati previously taught anthropology and sociology at the University of Groningen. From September 2026, he teaches in the International Relations and International Organization department. His teaching spans cultural anthropology, sociology of religion, heritage and memory, globalization and conflict, ethnicity, social movements, and research methods at both undergraduate and postgraduate levels.',
          ]}
        />
        <h3 className="font-semibold text-stone-900 mt-6 mb-4 not-prose">Selected Media & Podcasts</h3>
        <ProfileBullets
          items={[
            'Podcast: Cultural healing and humanistic approaches to health and wellbeing, Global Health Matters series, IIAS Leiden (November 2023)',
            'Podcast: Unmasking the Prejudice and Politics of Food in Northeast India, The Chicken Neck Podcast (October 2023)',
            'Interview: "Explained: What is Axone?", The Indian Express (June 2020)',
            'Interview: "Indian Cuisines that have Weathered Climatic Extremities", Whetstone Magazine (September 2020)',
          ]}
        />
        <h3 className="font-semibold text-stone-900 mt-6 mb-4 not-prose">Editorial Work</h3>
        <ProfileBullets
          items={[
            'Associate Editor, The Highlander Journal (since September 2020)',
            'Reviewer for: HAU: Journal of Ethnographic Theory, Oxford Research Encyclopaedia of Asian History, Society and Culture in South Asia, The Journal of Contemporary Asia, The South Asianist',
            'Case Reviewer, Contested Histories, EuroClio - European Association of History Educators (since April 2022)',
          ]}
        />
      </ProfileSection>

      {/* Contact & Scholarly Infrastructure */}
      <ProfileContactSection
        email={person.email}
        phone="+31623695126"
        role="Senior Research Associate & Programme Lead (Global Research, Outreach and Capacity-Building), The Highland Institute; Affiliate Research Fellow, IIAS Leiden"
        affiliationLines={[
          'International Institute for Asian Studies (IIAS)',
          'Leiden, The Netherlands'
        ]}
        testidPrefix="aditya"
        scholarlyProfiles={[
          { label: 'Academia.edu', url: 'https://graduateinstitute.academia.edu/AdityaKiranKakati', testid: 'aditya-academia-link' },
          { label: 'ORCID', url: 'https://orcid.org/0000-0003-0477-1593', testid: 'aditya-orcid-link' },
        ]}
      />
    </StaffProfileTemplate>
  );
};

export default AdityaKiranKakatiProfile;
